import { useEffect, useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { Target } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth-context";
import { Navbar } from "@/components/Navbar";
import { Progress } from "@/components/ui/progress";
import { pageHead } from "@/lib/seo";

type Mission = {
  user_id: string;
  mission_id: string;
  title: string;
  description: string;
  emoji: string;
  goal: number;
  category: string;
  sort_order: number;
  progress: number;
};

export const Route = createFileRoute("/missions")({
  head: () => pageHead({
    path: "/missions",
    title: "Reading missions and badges — Book Swap",
    description: "Complete reading missions, earn badges for swapping and lending, and show your contribution to the local book community.",
  }),
  component: Missions,
});

function Missions() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [missions, setMissions] = useState<Mission[]>([]);

  useEffect(() => { if (!authLoading && !user) navigate({ to: "/login" }); }, [authLoading, user, navigate]);

  useEffect(() => {
    if (!user) return;
    supabase.from("user_mission_progress").select("*").eq("user_id", user.id).order("sort_order")
      .then(({ data }) => setMissions((data as Mission[]) ?? []));
  }, [user]);

  if (authLoading || !user) return <div className="min-h-screen bg-background"><Navbar /></div>;

  const earned = missions.filter((m) => m.progress >= m.goal).length;

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <div className="mx-auto max-w-4xl px-4 py-10">
        <div className="flex items-center gap-3">
          <Target className="h-8 w-8 text-primary" />
          <h1 className="text-3xl font-bold">Reading Missions</h1>
        </div>
        <p className="mt-2 text-sm text-muted-foreground">
          Complete missions to earn badges. {earned}/{missions.length} earned.
        </p>

        <div className="mt-8 grid gap-4 sm:grid-cols-2">
          {missions.map((m) => {
            const pct = Math.min(100, Math.round((m.progress / m.goal) * 100));
            const done = m.progress >= m.goal;
            return (
              <div key={m.mission_id}
                className={`rounded-2xl p-5 shadow-[var(--shadow-card)] transition ${done ? "bg-gradient-to-br from-primary to-primary/80 text-white" : "bg-card"}`}>
                <div className="flex items-start gap-3">
                  <div className={`flex h-12 w-12 items-center justify-center rounded-full text-2xl ${done ? "bg-white/20" : "bg-primary/10"}`}>
                    {m.emoji}
                  </div>
                  <div className="min-w-0 flex-1">
                    <h3 className="font-semibold">{m.title}</h3>
                    <p className={`text-xs ${done ? "text-white/80" : "text-muted-foreground"}`}>{m.description}</p>
                  </div>
                  {done && <span className="rounded-full bg-white/20 px-2 py-1 text-xs font-bold">EARNED</span>}
                </div>
                <div className="mt-4 space-y-1">
                  <Progress value={pct} className={done ? "bg-white/20" : ""} />
                  <p className={`text-xs ${done ? "text-white/80" : "text-muted-foreground"}`}>
                    {Math.min(m.progress, m.goal)} / {m.goal}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
